/**
 * Building the search index without the screen noticing.
 *
 * The migration creates the two tables EMPTY, because it has to: a document is
 * rendered from an entry's values and its record's schema by JavaScript, and no
 * statement in a migration can call that. So the index is built after launch,
 * here.
 *
 * **The whole design problem is the JS thread.** expo-sqlite's sync API runs on
 * it, and rendering fifty thousand documents takes seconds. Done in one call
 * that is a frozen app. So this drives the rebuild one chunk at a time and
 * hands the thread back between chunks, which turns a freeze into work nobody
 * sees.
 *
 * **Resume is a stamp and a cursor.** Each record gets `search_ver:<recordId>`
 * in sync_meta when it is finished. A pass asks for the records without a
 * current stamp, so a kill mid-way, a crash, or a relaunch all come back by
 * simply asking again. Inside a record, `search_at:<recordId>` says how far the
 * last pass came, so a record of lakhs of rows goes on from there rather than
 * from its first row. Anything that marks a record stale removes both, and a
 * chunk that finds its cursor gone starts the record again (see
 * `searchRebuildChunk` in entry-repo.ts).
 *
 * Three things call a pass: the first launch after the migration, every
 * foreground, and a field-set change (which marks a record stale rather than
 * rebuilding it inline, so a pull can never block).
 */
import {
  finishSearchRebuild,
  recordsNeedingSearch,
  searchRebuildChunk,
  searchRebuildIds,
  searchRebuildStart,
} from '@/lib/entry-repo';
import { REBUILD_CHUNK, REBUILD_ID_BLOCK } from '@/lib/search-query';
import { useSearchIndexStore } from '@/stores/search-index-store';
import { useSyncStore } from '@/stores/sync-store';

/**
 * Give the screen the thread back.
 *
 * `requestIdleCallback` and NOT `InteractionManager.runAfterInteractions`. Two
 * reasons, and the first one is measured: React Native deprecated
 * InteractionManager in this SDK and prints a warning about it on every launch,
 * which a real device confirmed. The second is that idle is the better signal
 * anyway — it waits for the JS thread to be free, which is exactly the resource
 * a chunk is about to take, rather than for animations to finish.
 *
 * **A timer turn first, then idle.** Idle alone starved every timer in the
 * app: on the iOS simulator a 16 ms interval did not fire once in the 25 s a
 * pass of 558 chunks took, because an idle callback that asks for the next
 * one is served before any timer is. The sync engine waits on `setTimeout`
 * between the parts of a page, so a pass held a sync up too. A zero timeout
 * queues behind the timers already due, so they run, and then idle waits for
 * the frame's free time.
 *
 * The fallback is the timeout alone, so this still yields on any runtime that
 * does not provide idle callbacks. Yielding late is slow; not yielding is a
 * frozen app.
 */
type IdleHost = { requestIdleCallback?: (callback: () => void) => unknown };

const yieldToScreen = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => {
      const idle = (globalThis as IdleHost).requestIdleCallback;
      if (typeof idle === 'function') idle(() => resolve());
      else resolve();
    }, 0);
  });

/**
 * Only one pass at a time.
 *
 * Every foreground starts one, and a person switching apps twice in a second
 * would otherwise have two passes writing the same documents. Module state and
 * not store state, because this is a lock and not something a screen draws.
 */
let running = false;

/**
 * A call that arrived while a pass was running.
 *
 * The call is not simply dropped: a download that ended mid-pass calls here,
 * and the records it marked stale after the pass had listed its work would
 * wait for the next foreground. So the pass runs once more when it ends.
 */
let again = false;

/**
 * A hard stop on the number of chunks one pass will do.
 *
 * Not a limit anybody should reach in one pass, and a stopped pass goes on
 * from its cursor at the next one.
 * It is here so that a bug which stopped `searchRebuildChunk` from making
 * progress cannot spin forever on a person's battery.
 */
const CHUNK_CEILING = 10_000;

/**
 * How long one chunk may hold the thread, and the fewest rows a chunk takes.
 *
 * The chunk size follows the phone: the iOS simulator writes a hundred rows in
 * about 35 ms, the Android emulator in about 87 ms. A slow chunk halves the
 * next one and a quick one doubles it, between the floor and REBUILD_CHUNK.
 * The floor keeps a slow phone making progress rather than committing one row
 * at a time.
 *
 * 50 ms, below what a tap feels. At 32 ms the emulator fell to 25 rows a chunk
 * and about 700 rows a second, since every chunk also waits for a frame.
 */
const CHUNK_BUDGET_MS = 50;
const CHUNK_FLOOR = 25;

const nextChunkSize = (size: number, tookMs: number): number => {
  if (tookMs > CHUNK_BUDGET_MS) return Math.max(CHUNK_FLOOR, Math.floor(size / 2));
  if (tookMs < CHUNK_BUDGET_MS / 2) return Math.min(REBUILD_CHUNK, size * 2);
  return size;
};

/** A long download is in flight. It marks records stale page after page. */
const downloading = (): boolean => useSyncStore.getState().download !== null;

/** How one record's part of a pass ended. */
type RecordOutcome = 'done' | 'blocked' | 'stopped';

/**
 * One record, a chunk at a time, from its cursor.
 *
 * `'blocked'`: no schema has arrived for it yet. `'stopped'`: the pass must end
 * now, for a download or the chunk ceiling. `'done'`: every row has its
 * document, and the record is stamped unless it went stale meanwhile.
 */
const rebuildRecord = async (
  recordId: string,
  budget: { chunks: number; size: number },
): Promise<RecordOutcome> => {
  let after = searchRebuildStart(recordId);
  if (after === null) return 'blocked';
  for (;;) {
    await yieldToScreen();
    if (downloading()) return 'stopped';
    const places = searchRebuildIds(recordId, after, REBUILD_ID_BLOCK);
    if (places.length === 0) break;
    // Stepped by the rows each chunk took, not by the size: the size changes
    // after every chunk, and a step of the new size would skip rows or repeat them.
    for (let at = 0; at < places.length; ) {
      await yieldToScreen();
      if (downloading() || budget.chunks >= CHUNK_CEILING) return 'stopped';
      const part = places.slice(at, at + budget.size);
      const began = Date.now();
      const wrote = searchRebuildChunk(recordId, after, part);
      budget.chunks += 1;
      const tookMs = Date.now() - began;
      if (wrote === null) return 'blocked';
      if (wrote === 'stale') {
        // The cursor moved under the pass. A stale mark removed it, so the
        // record starts again at its first row; or another writer moved it
        // (two app processes for a moment during a relaunch, seen on Android),
        // so the pass goes on from where that one got to.
        const restart = searchRebuildStart(recordId);
        if (restart === null) return 'blocked';
        after = restart;
        break;
      }
      after = part[part.length - 1]!;
      at += part.length;
      budget.size = nextChunkSize(part.length, tookMs);
    }
  }
  // A blocked record is left unstamped, so the next pass returns to it once
  // its schema lands. An EMPTY one is stamped, or every pass would revisit it
  // forever. This is the whole reason a chunk answers null.
  finishSearchRebuild(recordId, after);
  return 'done';
};

/**
 * Index every record that needs it, yielding between chunks.
 *
 * Safe to call at any time and from anywhere. It returns as soon as there is
 * nothing to do, which is the normal case on every launch after the first.
 */
export const runSearchBackfill = async (first?: string): Promise<void> => {
  if (running) {
    again = true;
    return;
  }
  // Not during a long download. It writes rows without documents and marks
  // their records stale page after page, so a pass now would index a record
  // only to have the next page mark it stale again. The engine calls this
  // itself when the download ends.
  if (downloading()) return;
  running = true;
  again = false;

  const store = useSearchIndexStore.getState();
  try {
    const waiting = recordsNeedingSearch();
    /*
     * **`first` moves a record to the head of the queue; it cannot jump the
     * lock.** A pass already running returns above, so a search page opened
     * during the launch sweep — which is exactly when a record is most likely
     * to be unindexed — waits for that sweep to reach it on its own schedule.
     * The page says so in words while it waits, and `useSearchVersion` clears
     * the line the moment this record is stamped. Reworking the lock so a
     * second pass could interleave is a bigger change than a queue order, and
     * two passes writing the same documents is what the lock is for.
     */
    const pending =
      first === undefined || !waiting.includes(first)
        ? waiting
        : [first, ...waiting.filter((one) => one !== first)];
    store.beginPass(pending.length);
    if (pending.length === 0) return;

    const budget = { chunks: 0, size: REBUILD_CHUNK };
    for (const recordId of pending) {
      const outcome = await rebuildRecord(recordId, budget);
      useSearchIndexStore.getState().finishRecord();
      if (outcome === 'stopped') break;
    }
  } finally {
    useSearchIndexStore.getState().endPass();
    running = false;
  }
  if (again) void runSearchBackfill();
};
